import React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InfoIcon from '@mui/icons-material/Info';

import DialogShell from '@/components/DialogShell';
import { useUIStore } from '@/stores/useUIStore';

const AppInfoDialog: React.FC = () => {
  const open = useUIStore((s) => s.infoDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);

  const handleClose = () => closeDialog('infoDialog');

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      title="Info"
      titleColor="info.main"
      icon={<InfoIcon color="info" sx={{ fontSize: 28 }} />}
      maxWidth={650}
      actions={
        <Button variant="contained" color="info" onClick={handleClose}>
          OK
        </Button>
      }
    >
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <li>
          <Typography variant="body2">
            This tool is designed for A1, B1, C1, D1, BYD EV Voided, and BYD EV Solid bushings with straight outer and inner profiles.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Enter packaging data and targets manually, or upload a standard SOR Excel file. If the SOR contains multiple parts, select the required part number.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Verify parsed values after upload, as Excel formatting inconsistencies may populate incorrect data.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Select core/outer materials, calculate the minimum OD, and configure Target Stiffness Priority.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            The minimum core ID is calculated automatically from the control arm ID but can be adjusted manually.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Parameters outside the trained data range are highlighted in <strong style={{ color: '#d32f2f' }}>red</strong>. Computation can still proceed.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Default Shore hardness is <strong>55 ShA</strong>. Adjust it if the required stiffness is not achieved.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            The model is trained with a <strong>1 mm hex mesh (A1/B1/C1)</strong> and <strong>2 mm tet mesh (D1)</strong>. Different mesh settings during FEA validation may produce different results.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Clicking <strong>Compute (&gt;)</strong> generates design samples, automatically selects the best sample, and populates the fields.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            After a sample is selected, wait until its <strong>Sample ID</strong> appears in the scatter plot before continuing.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Avoid using other features while sample selection and field population are in progress.
          </Typography>
        </li>
        <li>
          <Typography variant="body2" component="div">
            <strong>Default Best Sample Criteria:</strong>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              <li>Radial stiffness within ±5% of target.</li>
              <li>Torsional stiffness below target.</li>
              <li>Minimum strain at 1 mm among remaining samples.</li>
              <li>Axial stiffness within soft limits.</li>
              <li>Conical stiffness used as a reference.</li>
            </Box>
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Material and test amplitude are required only when generating test values.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            In <strong>Explore Designs</strong>, you can filter, sort, and export generated designs to Excel.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Use the search bar to find a sample by <strong>Sample ID</strong> (e.g., <code>N0709</code>).
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            View the generated bush as a 3D preview.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Available downloads: <strong>PDF, PPT, STP, Marc, Synera, and NX Expressions</strong>.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            NX Expressions can be imported into TCE templates for CAD and drawings.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Use the <strong>Feedback</strong> button to send feedback or report issues via email.
          </Typography>
        </li>
      </Box>
    </DialogShell>
  );
};

export default AppInfoDialog;
